import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import BillingSidebarNav from '@/components/BillingSidebarNav';
import DesktopSidebarNav from '@/components/DesktopSidebarNav';
import GlobalNavHeader from '@/components/GlobalNavHeader';
import GlobalSearch from '@/components/GlobalSearch';
import { CHROME_EXTENSION_ORIGIN, ChromeExtensionMessages } from '@/constants/chromeExtension';
import { Color } from '@/constants/color';
import { useSidebarLayout } from '@/contexts/sidebarLayoutContext';
import useChromeExtensionAction from '@/hooks/useChromeExtensionAction';
import type { Action, GenericMessage } from '@/types/chromeExtension';
import Modal from '@/ui/components/Modal';
import Spinner from '@/ui/components/Spinner';
import { stripAgentPrefix } from '@/utils/resolveCurrentNavLabel';

const OverlayIcon = () => (
  <FontAwesomeIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" icon={faWarning} />
);

const RootHandler = () => {
  const [supportedDomain, setSupportedDomain] = useState(true);
  const [overlayDismissed, setOverlayDismissed] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const { contentOffsetClass } = useSidebarLayout();
  const { pathname } = useLocation();

  // Person mounts its own rail from the Person layout route, where the person data lives.
  // Billing has no layout route, so its rail is matched here.
  const inBillingSection = stripAgentPrefix(pathname).startsWith('/billing');

  const { currentAction, isLoading, reRunAction, setAction } = useChromeExtensionAction();

  const updateAction = useCallback(
    (data: Action | null) => {
      setAction(data);
    },
    [setAction]
  );

  const updateCurrentDomain = useCallback((data: GenericMessage) => {
    setSupportedDomain(data.supportedDomain as boolean);
    if (data.supportedDomain) setOverlayDismissed(false);
  }, []);

  const handleNewMessage = useCallback(
    (event: MessageEvent) => {
      const { data, origin } = event;

      if (origin === CHROME_EXTENSION_ORIGIN) {
        if (!data) return updateAction(null);

        if ('type' in data)
          switch (data.type) {
            case ChromeExtensionMessages.NEW_ACTION:
              return updateAction(data);
            case ChromeExtensionMessages.DOMAIN_CHANGED:
              return updateCurrentDomain(data);
          }
      }
    },
    [updateAction, updateCurrentDomain]
  );

  useEffect(() => {
    window.parent.postMessage({ type: ChromeExtensionMessages.DUGOUT_LOADED }, '*');

    window.addEventListener('message', handleNewMessage);

    return () => {
      window.removeEventListener('message', handleNewMessage);
    };
  }, [handleNewMessage]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore key repeats and skip if focused on editable elements
      if (event.repeat) return;

      const targetNode = event.target as Node | null;
      const targetElement =
        targetNode && targetNode.nodeType === Node.ELEMENT_NODE
          ? (targetNode as HTMLElement)
          : null;
      const isEditable =
        !!targetElement &&
        ((targetElement instanceof HTMLElement &&
          (targetElement.tagName === 'INPUT' ||
            targetElement.tagName === 'TEXTAREA' ||
            targetElement.isContentEditable)) ||
          targetElement.closest(
            '[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]'
          ));
      if (isEditable) return;

      const isMac = /mac/i.test(navigator.platform);
      const isKeyK = event.code === 'KeyK';
      const isMacKeyCombination = isMac && event.metaKey && !event.ctrlKey;
      const isWindowsKeyCombination = !isMac && event.ctrlKey && !event.metaKey;

      // Cmd+K on Mac (only), Ctrl+K on Windows/Linux (only)
      if (isKeyK && (isMacKeyCombination || isWindowsKeyCombination)) {
        event.preventDefault();
        setShowGlobalSearch((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleChromeExtensionReload = useCallback(() => {
    if (currentAction === null)
      window.parent.postMessage(
        {
          type: ChromeExtensionMessages.EXTRACT_STATE
        },
        '*'
      );
    else reRunAction();
  }, [currentAction, reRunAction]);

  return isLoading ? (
    <div className="flex items-center p-3">
      <Spinner label="Loading ..." />
    </div>
  ) : (
    <>
      <DesktopSidebarNav
        onOpenGlobalSearch={() => setShowGlobalSearch(true)}
        originUrl={currentAction?.origin}
      />
      {inBillingSection && <BillingSidebarNav />}
      <div className={contentOffsetClass}>
        {/*
          The bar hides itself above `lg`, where the sidebar carries these actions. It has to do
          that itself rather than sit in a wrapper: it is pinned to the top, and a wrapper only as
          tall as the bar would give it nothing to stay pinned over.
        */}
        <GlobalNavHeader
          currentAction={currentAction}
          resetActiveTab={handleChromeExtensionReload}
          onOpenGlobalSearch={() => setShowGlobalSearch(true)}
        />
        <Outlet />
      </div>
      {showGlobalSearch && <GlobalSearch onClose={() => setShowGlobalSearch(false)} />}
      <Modal
        blurredBackground
        button={{
          children: 'Yes, open anyway',
          color: Color.YELLOW,
          size: 'sm'
        }}
        icon={<OverlayIcon />}
        iconBackgroundColor="bg-yellow-100"
        open={!overlayDismissed && !supportedDomain}
        restrictCloseToButton
        onClose={() => setOverlayDismissed(true)}
        textContent="Would you like to see it?"
        title="Dugout is not supported here"
      />
    </>
  );
};

export default RootHandler;
